import { useEffect } from "react";

type ShortcutHandlers = {
  onNewNote?: () => void;
  onOpenSearch?: () => void;
  onToggleTheme?: () => void;
  enabled?: boolean;
};

const EDITABLE_TAGS = new Set([
  "INPUT",
  "TEXTAREA",
  "SELECT",
  "OPTION",
  "BUTTON",
]);

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  return false;
};

export const useKeyboardShortcuts = ({
  onNewNote,
  onOpenSearch,
  onToggleTheme,
  enabled = true,
}: ShortcutHandlers) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (isEditableTarget(event.target)) return;

      const isCtrlOrMeta = event.ctrlKey || event.metaKey;
      if (!isCtrlOrMeta) return;

      const key = event.key.toLowerCase();

      if (key === "n" && !event.shiftKey) {
        if (onNewNote) {
          event.preventDefault();
          onNewNote();
        }
        return;
      }

      if (key === "k" && !event.shiftKey) {
        if (onOpenSearch) {
          event.preventDefault();
          onOpenSearch();
        }
        return;
      }

      if (key === "l" && event.shiftKey) {
        if (onToggleTheme) {
          event.preventDefault();
          onToggleTheme();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, onNewNote, onOpenSearch, onToggleTheme]);
};

export default useKeyboardShortcuts;
